import { ChevronRightIcon } from "@heroicons/react/outline";
import Link from "next/link";
import React from "react";
import { cn } from "ui/lib/utils";

type ListRowProps = {
  title: React.ReactNode;
  /** One line under the title: the two or three facts this list is for. */
  subtitle?: React.ReactNode;
  /**
   * Fixed-width cells after the title from md up, for the facts a wider
   * screen can line up instead of folding into the subtitle.
   */
  columns?: { content: React.ReactNode; className?: string }[];
  leading?: React.ReactNode;
  /** Kept outside the row's tap target so it can hold its own button. */
  trailing?: React.ReactNode;
  /** Inside the tap target at the end, e.g. a status pill or a + that is part of the tap. */
  meta?: React.ReactNode;
  /** A chevron at the end of the tap target, for rows that push a screen. */
  chevron?: boolean;
  /** Extra attributes for the tap target (a name, a radio role). */
  targetProps?: React.ButtonHTMLAttributes<HTMLButtonElement>;
  href?: string;
  onClick?: () => void;
  active?: boolean;
  /** aria-current for the row's tap target, e.g. "date" for today. */
  current?: React.AriaAttributes["aria-current"];
  className?: string;
};

/**
 * The phone stand-in for a table row: the whole row opens the item, the title
 * wraps to two lines instead of clipping, and the press state shows on touch.
 */
export const ListRow = ({
  title,
  subtitle,
  columns,
  leading,
  trailing,
  meta,
  chevron,
  targetProps,
  href,
  onClick,
  active,
  current,
  className,
}: ListRowProps) => {
  const body = (
    <>
      {leading ? <span className="shrink-0">{leading}</span> : null}
      <span className="min-w-0 flex-1">
        <span className="line-clamp-2 text-[0.9375rem] font-medium leading-snug text-white/90">
          {title}
        </span>
        {subtitle ? (
          <span className="mt-0.5 block truncate text-[0.8125rem] text-faint">
            {subtitle}
          </span>
        ) : null}
      </span>
      {columns?.map((column, i) => (
        <span
          key={i}
          className={cn(
            "shrink-0 truncate text-[0.8125rem] text-white/60 max-md:hidden",
            column.className
          )}
        >
          {column.content}
        </span>
      ))}
      {meta ? <span className="shrink-0">{meta}</span> : null}
      {chevron ? (
        <span className="flex w-11 shrink-0 justify-center">
          <ChevronRightIcon aria-hidden="true" className="size-4 text-faint" />
        </span>
      ) : null}
    </>
  );
  const target = cn(
    "-ml-2 flex min-w-0 flex-1 items-center gap-3 self-stretch rounded-xl py-2 pl-2 text-left",
    (href || onClick) &&
      "cursor-pointer transition-colors active:bg-white/[0.05]"
  );

  return (
    <div
      aria-current={active ? "true" : undefined}
      className={cn(
        "flex min-h-16 items-center gap-2 rounded-xl px-3",
        active && "bg-white/[0.08]",
        className
      )}
    >
      {href ? (
        <Link href={href} aria-current={current} className={target}>
          {body}
        </Link>
      ) : onClick ? (
        <button
          type="button"
          {...targetProps}
          onClick={onClick}
          aria-current={current}
          className={target}
        >
          {body}
        </button>
      ) : (
        <div className={target}>{body}</div>
      )}
      {/* The 44px trailing column: a chevron, ⋯, add or check centres on the same x. */}
      {trailing ? (
        <span className="flex min-w-11 shrink-0 items-center justify-center">
          {trailing}
        </span>
      ) : null}
    </div>
  );
};
